<template>
  <ChannelSection>
    <template #title>
      <slot name="title">{{ sectionLabel }}</slot>
    </template>

    <template #actions>
      <slot name="actions"></slot>
      <ContestNewDropdown>
        <template #trigger>
          <Button
            v-if="channelStore.permissions.manageContests"
            type="button"
            variant="icon"
            class="btn-circle border-border-brand size-10"
          >
            <Icon class="text-text size-6" icon="ic:baseline-plus" />
          </Button>
        </template>
      </ContestNewDropdown>
    </template>

    <div v-if="!loading" class="relative">
      <ChannelRailVirtualized
        ref="railRef"
        :items="contestsCasted"
        :get-key="(contest) => contest.id"
        :item-width="312"
      >
        <template #default="{ item: { data: contest } }">
          <RaffleItemCard
            v-if="contest.__typename === 'Raffle'"
            :id="idify(contest.id)"
            :raffle="contest"
            :loading="loading"
            :authorization-rule-group-fragment="
              contest.authorization_rule_group
            "
            @join="refetch"
            @refetch="refetch"
          />
          <QuizItemCard
            v-else-if="contest.__typename === 'Quiz'"
            :id="idify(contest.id)"
            :quiz="contest"
            :loading="loading"
            :authorization-rule-group-fragment="
              contest.authorization_rule_group
            "
            @refetch="refetch"
          />
        </template>
      </ChannelRailVirtualized>

      <div
        v-if="loadingMore"
        class="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-gradient-to-l from-brand-bg-main via-brand-bg-main/80 to-transparent pl-8 pr-5"
      >
        <Spinner class="size-4" />
      </div>
    </div>

    <SkeletonRail v-else :count="3">
      <template #default="{ index }">
        <ContestCardSkeleton :index="index" />
      </template>
    </SkeletonRail>
  </ChannelSection>
</template>

<script setup lang="ts">
import RaffleItemCard, { RaffleItemCardFragment } from "./raffle-item-card.vue";
import ChannelSection from "../channel-section.vue";
import { computed, ref } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import QuizItemCard, { QuizItemCardFragment } from "./quiz-item-card.vue";
import ContestNewDropdown from "./creation/contest-new-dropdown.vue";
import Button from "@/components/ui/button/button.vue";
import { Icon } from "@iconify/vue/dist/iconify.js";
import { idify } from "@/utils/misc";
import ChannelRailVirtualized from "../channel-rail-virtualized.vue";
import { useInfiniteScroll } from "@vueuse/core";
import SkeletonRail from "@/components/ui/skeleton/skeleton-rail.vue";
import ContestCardSkeleton from "@/components/ui/skeleton/contest-card-skeleton.vue";
import Spinner from "@/components/ui/spinner.vue";
import { useAppStore } from "@/stores/app.store";
import { useI18n } from "vue-i18n";
import { ContestsForChannelDocument } from "@/generated/graphql";
import type { ResultOf } from "@graphql-typed-document-node/core";

type RaffleItemCardData = ResultOf<typeof RaffleItemCardFragment>;
type QuizItemCardData = ResultOf<typeof QuizItemCardFragment>;

const appStore = useAppStore();
const { t } = useI18n();

const channelStore = useChannelStore();
const railRef = ref<HTMLElement | null>(null);

useInfiniteScroll(
  railRef,
  () => {
    emit("loadMore");
  },
  {
    direction: "right" as const,
    distance: 100,
    interval: 300,
  },
);

const emit = defineEmits<{
  refetch: [];
  loadMore: [];
}>();

const props = defineProps<{
  loading: boolean;
  contests: ResultOf<typeof ContestsForChannelDocument>["contestsForChannel"];
  loadingMore?: boolean;
}>();

// Cast contests to properly typed union
const contestsCasted = computed(() => {
  return (
    props.contests?.map((contest) => {
      if (contest.__typename === "Raffle") {
        return contest as RaffleItemCardData;
      } else {
        return contest as QuizItemCardData;
      }
    }) ?? []
  );
});

function refetch() {
  emit("refetch");
}

const sectionLabel = computed(() => {
  if (appStore.isEmbed) {
    return t("channel.titles.embedContests");
  }
  return t("channel.titles.contests");
});
</script>
